<template>
  <nuxt-link
    class="d-flex align-center ga-4 px-6 py-4 text-decoration-none notification-link"
    :class="{ 'notification-item': !borderless }"
    :to="actionPath"
    @click="onClick"
  >
    <div
      class="notification-icon flex-shrink-0 rounded-circle d-flex align-center justify-center"
      :class="colors.bg"
    >
      <v-icon :color="colors.icon" :icon="icon.icon" :size="icon.size || 24" />
    </div>
    <div class="notification-body flex-grow-1">
      <div class="d-flex align-center ga-3">
        <span class="qtm-label text-dark-grey flex-grow-1 text-truncate" v-text="notification.title" />
        <span class="qtm-overline text-mid-grey text-no-wrap" v-text="onlyTime(notification.date_created)" />
      </div>
      <div class="d-flex align-center ga-2 notification-sub-info">
        <template v-for="(item, i) in notification.content" :key="i">
          <span
            v-if="i > 0"
            class="notification-dot bg-mid-grey rounded-circle flex-shrink-0"
          />
          <span
            class="qtm-label-small text-mid-grey"
            :class="i === 0 ? 'notification-first-sub' : 'notification-sub-item'"
            v-text="item"
          />
        </template>
      </div>
    </div>
  </nuxt-link>
</template>

<script setup lang="ts">
import type { Notification } from '@quotetome/materials-api'
import { onlyTime } from '@/models/filters'

export interface Props {
  borderless?: boolean
  notification: Notification
}

const props = defineProps<Props>()
const emit = defineEmits(['click'])

const skipPreviousRoute = useState('skipPreviousRoute', () => false)
const router = useRouter()

const onClick = () => {
  skipPreviousRoute.value = true
  emit('click')
}

const actionPath = computed(() => {
  try {
    const url = new URL(props.notification.action_url)
    const path = url.pathname + url.search + url.hash
    const resolved = router.resolve(path)

    if (resolved.matched.length > 0) {
      return path
    }
  }
  catch {
    // invalid URL, fall through
  }

  return props.notification.action_url
})

const icon = computed<{ icon: string; size?: number }>(() => {
  if (props.notification.comment) {
    return { icon: 'ms:comment', size: 20 }
  }

  const templateMap = {
    incomplete_order_reminder: { icon: 'ms:bolt' },
    in_store_purchase_exceeds_limit: { icon: 'ms:bolt' },
    no_quotes_returned: { icon: 'ms:shoppingmode' },
    message_supplier_to_user: { icon: 'ms:mail' },
    po_approval_request_cancelled: { icon: 'ms:package_2' },
    quotes_ready: { icon: 'ms:shoppingmode' },
  }

  if (props.notification.template_name in templateMap) {
    return templateMap[props.notification.template_name as keyof typeof templateMap]!
  }

  if (props.notification.template_name === 'receive_order_reminder') {
    return props.notification.title?.includes('pickup')
      ? { icon: 'ms:package_2' }
      : { icon: 'ms:local_shipping' }
  }

  if (props.notification.template_name === 'po_issued' && props.notification.title?.includes('signed')) {
    return { icon: 'ms:ink_pen' }
  }

  const objectMap = {
    expense: { icon: 'ms:receipt_long' },
    invoice: { icon: 'ms:attach_money' },
    order: { icon: 'ms:package_2' },
    requisition: { icon: 'ms:shopping_cart' },
  }

  return objectMap[props.notification.action_content_type as keyof typeof objectMap] ?? { icon: 'ms:notifications' }
})

const colors = computed(() => {
  const statusMap = {
    error: { icon: 'error-info', bg: 'bg-error-lighten-1' },
    success: { icon: 'success-info', bg: 'bg-success-info-lighten-1' },
    warning: { icon: 'attention', bg: 'bg-attention-lighten-1' },
  }

  if (props.notification.status && props.notification.status in statusMap) {
    return statusMap[props.notification.status as keyof typeof statusMap]
  }

  if (props.notification.comment || props.notification.template_name === 'message_supplier_to_user') {
    return { icon: 'neutral-grey', bg: 'bg-neutral-light-grey' }
  }

  return { icon: 'info', bg: 'bg-info-lighten-1' }
})
</script>

<style lang="scss" scoped>
.notification-icon {
  height: 40px;
  width: 40px;
}

.notification-body {
  min-width: 0;
}

.notification-dot {
  height: 3px;
  width: 3px;
}

.notification-first-sub {
  flex-shrink: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-sub-item {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-sub-info {
  min-width: 0;
  overflow: hidden;
}

.notification-link:hover {
  background: rgba(var(--v-border-color), var(--v-hover-opacity));
}

.notification-item {
  position: relative;

  &::after {
    border-bottom: 1px solid rgb(var(--v-theme-light-grey));
    bottom: 0;
    content: "";
    left: 80px;
    position: absolute;
    right: 0;
  }
}
</style>
